import { SortOrder } from '@map/enums';
import { AddFloorPlanDto, FloorPlan } from '@map/models';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeEntity, updateEntity, upsertEntities, upsertEntity, withEntities } from '@ngrx/signals/entities';
import { environment } from 'src/environments/environment';
import { computed, inject } from '@angular/core';
import { FloorPlanService } from '@map/services/floor-plan.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concatMap, forkJoin, mergeMap, of, pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { updateState, withDevtools, withDevToolsStub } from '@angular-architects/ngrx-toolkit';

type FloorPlanState = {
    selected: FloorPlan | null;
    editShape: boolean;
    filter: {
        query: string | null;
        order: SortOrder;
    };
    loadedFloorPlans: FloorPlan[];
    loading: boolean;
    loaded: boolean;
};

const initialState: FloorPlanState = {
    selected: null,
    editShape: false,
    filter: { query: '', order: SortOrder.Ascending },
    loadedFloorPlans: [],
    loading: false,
    loaded: false,
};

export const withTreeShakableDevTools = environment.production ? withDevToolsStub : withDevtools;

/** Returns true if the URL has no SAS expiry param, or if the expiry is still in the future. */
function isSasUrlValid(url: string): boolean {
    try {
        const match = url.match(/[?&]se=([^&]+)/);
        if (!match) return true;
        return new Date(decodeURIComponent(match[1])) > new Date();
    } catch {
        return false;
    }
}

export const FloorPlanStore = signalStore(
    { providedIn: 'root' },
    withTreeShakableDevTools('floor-plans'),
    withEntities<FloorPlan>(),
    withState<FloorPlanState>(initialState),
    withComputed(({ entities, filter }) => ({
        floorPlanCount: computed(() => entities().length),
        searchResults: computed(() => {
            return entities()
                .filter((floorPlan) => {
                    const matchesQuery = filter.query()
                        ? floorPlan?.title?.toLowerCase().includes(filter.query()?.toLowerCase() || '')
                        : true;
                    return matchesQuery;
                })
                .sort((a, b) => {
                    if (filter.order() === SortOrder.Ascending) {
                        return a.title?.localeCompare(b.title || '') || 0;
                    } else {
                        return b.title?.localeCompare(a.title || '') || 0;
                    }
                });
        }),
    })),
    withMethods((store, floorPlanService = inject(FloorPlanService)) => ({
        updateFilter: (filter: { query?: string | null; order?: SortOrder | null }) => {
            updateState(store, '[FloorPlan] Update Filter', {
                filter: {
                    query: filter.query ?? '',
                    order: filter.order || SortOrder.Ascending,
                },
            });
        },
        resetFilter: () => updateState(store, '[FloorPlan] Reset Filter', { filter: initialState.filter }),
        reset: () => updateState(store, '[FloorPlan] Reset Store', { selected: null, loadedFloorPlans: [] }),

        select: (floorPlan: FloorPlan | null) => {
            updateState(store, '[FloorPlan] Select', { selected: floorPlan });
        },
        selectId: rxMethod<{ id: number; fromServer?: boolean }>(
            pipe(
                switchMap(({ id, fromServer }) => {
                    if (fromServer) {
                        updateState(store, '[FloorPlan] Select By ID From Server', { loading: true });
                        return floorPlanService.get<FloorPlan>(id).pipe(
                            tapResponse({
                                next: (floorPlan) =>
                                    updateState(store, '[FloorPlan] Select By ID Success', { selected: floorPlan, loading: false }),
                                error: (error) => {
                                    console.error('Failed to select floor plan by ID', error);
                                    updateState(store, '[FloorPlan] Select By ID Failure', { loading: false });
                                },
                            })
                        );
                    } else {
                        const floorPlan = store.entityMap()[id] ?? null;
                        if (floorPlan) {
                            updateState(store, '[FloorPlan] Select By ID From Cache', { selected: floorPlan });
                            return of(floorPlan);
                        }
                        updateState(store, '[FloorPlan] Select By ID - Not Found', { selected: null });
                        return of(null);
                    }
                })
            )
        ),
        deselect: () => updateState(store, '[FloorPlan] Deselect', { selected: null }),
        getAll: rxMethod<void>(
            pipe(
                switchMap(() => {
                    if (store.entities().length > 0) return store.entities();
                    updateState(store, '[FloorPlan] Get All', { loading: true });
                    return floorPlanService.getAll<FloorPlan[]>().pipe(
                        tapResponse({
                            next: (floorPlans) => {
                                // Strip imageUrl — the list endpoint may return a non-signed or stale URL. Only getImageUrls (individual GET) populates valid signed URLs.
                                const stripped = floorPlans.map((fp) => ({ ...fp, imageUrl: undefined })) as FloorPlan[];
                                updateState(store, '[FloorPlan] Get All Success', upsertEntities(stripped), {
                                    loaded: true,
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Failed to load floor plans', error);
                                updateState(store, '[FloorPlan] Get All Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        create: rxMethod<AddFloorPlanDto>(
            pipe(
                switchMap((addFloorPlanDto) => {
                    updateState(store, '[FloorPlan] Create', { loading: true });
                    return floorPlanService.create<any>(addFloorPlanDto.floorPlan).pipe(
                        concatMap((newFloorPlan) => {
                            // We need to create a new FloorPlan object because the server returns a different object
                            const floorPlan = new FloorPlan(newFloorPlan);
                            updateState(store, '[FloorPlan] Create Success', upsertEntity(floorPlan), {
                                selected: floorPlan,
                            });
                            return floorPlanService.uploadImage(floorPlan.id, addFloorPlanDto.image).pipe(
                                tapResponse({
                                    next: () =>
                                        updateState(
                                            store,
                                            '[FloorPlan] Upload Image Success',
                                            // Clear cached imageUrl so the next getImageUrls call fetches a valid signed URL after the upload completes.
                                            updateEntity({ id: floorPlan.id, changes: () => ({ imageUrl: undefined }) }),
                                            { loading: false }
                                        ),
                                    error: (error) => {
                                        console.error('Failed to upload floor plan image', error);
                                        updateState(store, '[FloorPlan] Upload Image Failure', { loading: false });
                                    },
                                })
                            );
                        })
                    );
                })
            )
        ),
        update: rxMethod<{ floorPlan: FloorPlan; image?: File }>(
            pipe(
                switchMap(({ floorPlan, image }) => {
                    updateState(store, '[FloorPlan] Update', { loading: true });
                    return floorPlanService.patch<FloorPlan>(floorPlan.id, floorPlan).pipe(
                        concatMap(() => {
                            updateState(
                                store,
                                '[FloorPlan] Update Success',
                                updateEntity({
                                    id: floorPlan.id,
                                    changes: () => floorPlan,
                                })
                            );
                            // If image is provided, upload it as well
                            return image
                                ? floorPlanService.uploadImage(floorPlan.id, image).pipe(
                                      tapResponse({
                                          next: () =>
                                              updateState(
                                                  store,
                                                  '[FloorPlan] Upload Image Success',
                                                  // Clear cached imageUrl so the next getImageUrls call fetches the updated URL from the server.
                                                  updateEntity({ id: floorPlan.id, changes: () => ({ imageUrl: undefined }) }),
                                                  { loading: false }
                                              ),
                                          error: (error) => {
                                              console.error('Failed to upload floor plan image', error);
                                              updateState(store, '[FloorPlan] Upload Image Failure', { loading: false });
                                          },
                                      })
                                  )
                                : (updateState(store, '[FloorPlan] Update Complete', { loading: false }), of(null));
                        })
                    );
                })
            )
        ),
        delete: rxMethod<number>(
            pipe(
                switchMap((id) => {
                    updateState(store, '[FloorPlan] Delete', { loading: true });
                    return floorPlanService.delete(id).pipe(
                        tapResponse({
                            next: () => updateState(store, '[FloorPlan] Delete Success', removeEntity(id), { loading: false }),
                            error: (error) => {
                                console.error('Failed to delete floor plan', error);
                                updateState(store, '[FloorPlan] Delete Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        getImageUrls: rxMethod<{ floorPlanIds: number[] }>(
            pipe(
                mergeMap(({ floorPlanIds }) => {
                    if (floorPlanIds.length === 0) {
                        updateState(store, '[FloorPlan] Get Image URLs - Empty', { loadedFloorPlans: [] });
                        return of([]);
                    }
                    // Use cached imageUrls where available — only fetch from server for floor plans that don't yet have a valid (non-expired) imageUrl in the entity store.
                    const entityMap = store.entityMap();
                    const cached = floorPlanIds
                        .map((id) => entityMap[id])
                        .filter((fp): fp is FloorPlan => !!fp?.imageUrl && isSasUrlValid(fp.imageUrl));
                    const cachedIds = new Set(cached.map((fp) => fp.id));
                    const needsFetch = floorPlanIds.filter((id) => !cachedIds.has(id));

                    if (needsFetch.length === 0) {
                        updateState(store, '[FloorPlan] Get Image URLs From Cache', { loadedFloorPlans: cached });
                        return of(cached);
                    }

                    updateState(store, '[FloorPlan] Get Image URLs', { loading: true });
                    const requests = needsFetch.map((id) => floorPlanService.get<FloorPlan>(id));
                    return forkJoin(requests).pipe(
                        tapResponse({
                            next: (fetched) => {
                                const allFloorPlans = [...cached, ...fetched];
                                updateState(store, '[FloorPlan] Get Image URLs Success', upsertEntities(fetched), {
                                    loadedFloorPlans: allFloorPlans,
                                    loading: false,
                                });
                            },
                            error: (error) => {
                                console.error('Error fetching Floor Plans:', error);
                                updateState(store, '[FloorPlan] Get Image URLs Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        updateCoordinates: rxMethod<{ id: number; coordinates: number[][] }>(
            pipe(
                mergeMap(({ id, coordinates }) => {
                    const existing = store.entityMap()[id];
                    if (!existing) return of(null);
                    return floorPlanService.patch<FloorPlan>(id, { ...existing, coordinates }).pipe(
                        tapResponse({
                            next: () =>
                                updateState(
                                    store,
                                    '[FloorPlan] Update Coordinates Success',
                                    updateEntity({ id, changes: () => ({ coordinates }) })
                                ),
                            error: (error) => console.error('Failed to update floor plan coordinates', error),
                        })
                    );
                })
            )
        ),
        copy: rxMethod<{ sourceFloorPlanId: number; targetFloorIds: number[] }>(
            pipe(
                mergeMap(({ sourceFloorPlanId, targetFloorIds }) => {
                    updateState(store, '[FloorPlan] Copy', { loading: true });
                    return floorPlanService.get<FloorPlan>(sourceFloorPlanId).pipe(
                        switchMap((source) => {
                            const imageUrl = source.secureImageUrl || source.imageUrl;
                            if (!imageUrl) return of([]);
                            return floorPlanService.getImageBlob(imageUrl).pipe(
                                switchMap((blob) => {
                                    const imageFile = new File([blob], `floor-plan-copy.${blob.type.split('/')[1] || 'jpg'}`, {
                                        type: blob.type,
                                    });
                                    const creates = targetFloorIds.map((floorId) =>
                                        floorPlanService.create<any>({ ...source, id: 0, buildingFloorId: floorId }).pipe(
                                            concatMap((created) => {
                                                const newFloorPlan = new FloorPlan(created);
                                                return floorPlanService.uploadImage(newFloorPlan.id, imageFile).pipe(
                                                    tapResponse({
                                                        next: () =>
                                                            updateState(
                                                                store,
                                                                '[FloorPlan] Copy Upload Success',
                                                                upsertEntity(newFloorPlan)
                                                            ),
                                                        error: (error) => console.error('Failed to upload copied floor plan image', error),
                                                    })
                                                );
                                            })
                                        )
                                    );
                                    return forkJoin(creates);
                                })
                            );
                        }),
                        tapResponse({
                            next: () => updateState(store, '[FloorPlan] Copy Success', { loading: false }),
                            error: (error) => {
                                console.error('Failed to copy floor plan', error);
                                updateState(store, '[FloorPlan] Copy Failure', { loading: false });
                            },
                        })
                    );
                })
            )
        ),
        setEditFloorPlanShape: (editShape: boolean) => {
            updateState(store, '[FloorPlan] Set Edit Floor Plan Shape', { editShape });
        },
    }))
);
